/**
 * DrawerMention hook — unified @ autocomplete for the Matthew Drawer chat.
 *
 *   @User Name        — workspace member (avatar color badge)
 *   @Agent Name       — workspace agent (amber badge)
 *   @Skill Name       — workspace skill (accent badge)
 *   @tool_name        — platform/custom tool (info badge)
 *   @App Name         — workspace app (info badge)
 *   @Workflow Name    — workflow trigger (rose badge)
 *   @collection_name  — tool-queryable data collection (teal badge)
 *   @Context Block    — chat-enabled context block (violet badge)
 *
 * All mention types use @ as prefix, differentiated by icon/color.
 *
 * Candidates are pulled from the LiveComponent via pushEventTo
 * ("request_mention_candidates" → "update_mention_candidates"), mirroring the
 * App Builder ChatMention pattern.
 *
 * Dropdown lives on document.body to survive LiveView DOM patches.
 */
import { liveComponentTarget } from "./drawer_mention_target.mjs"
import { rankMentionCandidates } from "./drawer_mention_rank.mjs"
import {
  rememberMentionRef,
  pruneMentionRefs,
  serializeMentionRefs
} from "./drawer_mention_refs.mjs"

export default {
  mounted() {
    this.textarea = this.el
    this.candidates = []
    this.allUsers = []
    this.allAgents = []
    this.allSkills = []
    this.allTools = []
    this.allApps = []
    this.allWorkflows = []
    this.allCollections = []
    this.allContextBlocks = []
    this.mentionStart = -1
    this.selectedIdx = 0
    // Every mention accepted from the menu, as {kind, id, label}. The server
    // resolves by id, so a label with a comma or a period in it no longer has
    // to survive a second pass over the sentence.
    this.mentionRefs = []
    this._submittedMentionRefs = []
    this.isOpen = false
    this._skipNextBlur = false
    this._lastValue = this.textarea.value || ""
    this._clearDraftAfterSubmit = false
    this._preparedSubmitValue = null

    // The LiveComponent target (CID) — read from the closest [phx-target]
    // ancestor (the drawer form sets phx-target={@myself}). pushEventTo with
    // a CID routes the event to the component, not the parent LiveView.
    this.componentTarget = liveComponentTarget(this.textarea)
    this.form = this.textarea.closest("form")
    this.componentRoot = this.textarea.closest("#monica-drawer")
    this.submittedMessageInput = this.form?.querySelector("[data-drawer-submitted-message]")
    this.mentionRefsInput = this.form?.querySelector("[data-drawer-mention-refs]")

    // Listen for the candidate payload pushed back by push_mention_candidates/1
    this.handleEvent("update_mention_candidates", (payload) => {
      this.allUsers = payload.users || []
      this.allAgents = payload.agents || []
      this.allSkills = payload.skills || []
      this.allTools = payload.tools || []
      this.allApps = payload.apps || []
      this.allWorkflows = payload.workflows || []
      this.allCollections = payload.collections || []
      this.allContextBlocks = payload.context_blocks || []
      // Re-render overlay (mention highlights) with the new known set
      this._syncOverlay()
      // If user already typed @ before the round-trip arrived, refresh the dropdown
      if (this.isOpen) this._detectMention()
    })

    this.handleEvent("clear_drawer_input", (payload) => {
      if (payload?.id && payload.id !== this.textarea?.id) return
      this._acceptSubmittedClear()
    })

    // The server pushes this when it REFUSES a message the submit-time
    // machinery already wiped (e.g. no AI credential enabled). Resets the
    // submitted-draft flags too — otherwise _restoreDraftAfterPatch would
    // clear the restored text again on the next patch.
    this.handleEvent("restore_drawer_input", (payload) => {
      if (!this.textarea) return
      if (payload?.id && payload.id !== this.textarea.id) return

      const value = payload?.value || ""
      this._forgetSubmittedDraft()
      this._preparedSubmitValue = null
      this._clearDraftAfterSubmit = false
      this.mentionRefs = this._submittedMentionRefs || []
      this._setTextareaValue(value)
      this._lastValue = value
      this._resize()
      this._syncOverlay()
      this._syncMentionRefs()
    })

    // Request the initial candidate set
    if (Number.isInteger(this.componentTarget)) {
      this.pushEventTo(this.componentTarget, "request_mention_candidates", {})
    }

    this._createDropdown()
    this._resize()

    // Overlay for styled mention highlighting in the textarea.
    // Only flip the textarea color to transparent (so the overlay's styled
    // version takes over) when the overlay element actually exists. If the
    // overlay is missing for any reason in prod (stale asset, JS error before
    // mount, etc.), the textarea keeps its native text color and the user
    // still sees what they type — at worst losing the @mention badge styling.
    const overlayId = this.textarea.id.replace("drawer-input-", "drawer-mention-overlay-")
    this.overlay = document.getElementById(overlayId)
    if (this.overlay) {
      this.textarea.style.color = "transparent"
    }

    // A throw mid-handler (e.g. a candidate with a null name inside
    // _detectMention/_filter) used to leave the dropdown stuck open with
    // `dataset.mentionOpen` set — and the composer's inline onkeydown guard
    // then swallowed EVERY Enter. Fail closed instead.
    this._inputHandler = () => {
      try {
        this._rememberDraft()
        this._resize()
        this._detectMention()
        this._syncOverlay()
        this._syncMentionRefs()
      } catch (err) {
        console.error("[DrawerMention] input handler error:", err)
        try { this._close() } catch (_e) { /* keep the composer usable */ }
      }
    }
    this._keydownHandler = (e) => this._onKeydown(e)
    this._blurHandler = () => {
      if (this._skipNextBlur) {
        this._skipNextBlur = false
        return
      }
      setTimeout(() => {
        if (document.activeElement !== this.textarea) {
          this._close()
        }
      }, 150)
    }

    this._scrollHandler = () => {
      if (this.overlay) this.overlay.scrollTop = this.textarea.scrollTop
    }
    this._submitHandler = () => this._prepareSubmitClear()
    this._submitButtonHandler = (event) => this._prepareSubmitButtonClear(event)
    this._beforeComponentInteraction = (event) => {
      if (event.target === this.textarea || this.textarea.contains(event.target)) return
      if (event.target.closest("button[type='submit']")) return
      this._rememberDraft()
      this._syncDraftToServer()
    }

    this.textarea.addEventListener("input", this._inputHandler)
    this.textarea.addEventListener("keydown", this._keydownHandler)
    this.textarea.addEventListener("blur", this._blurHandler)
    this.textarea.addEventListener("scroll", this._scrollHandler)
    if (this.form) this.form.addEventListener("submit", this._submitHandler, true)
    if (this.form) this.form.addEventListener("pointerdown", this._submitButtonHandler, true)
    if (this.form) this.form.addEventListener("click", this._submitButtonHandler, true)
    if (this.componentRoot) {
      this.componentRoot.addEventListener("pointerdown", this._beforeComponentInteraction, true)
      this.componentRoot.addEventListener("change", this._beforeComponentInteraction, true)
    }

    this._restoreDraftAfterPatch()
    this._syncOverlay()
  },

  updated() {
    // Throwing inside updated() aborts the LiveView DOM patch. That used to
    // make new chat messages invisible until refresh. Swallow + log instead.
    try {
      // LiveComponent CIDs are ephemeral and may change when sibling drawers
      // mount/unmount. Never keep routing events to the CID captured at mount.
      this.componentTarget = liveComponentTarget(this.textarea)
      this._restoreDraftAfterPatch()
      this._resize()
      this._syncOverlay()
      this._syncMentionRefs()
      if (this.isOpen) this._detectMention()
    } catch (err) {
      console.error("[DrawerMention] updated() error:", err)
    }
  },

  destroyed() {
    if (this.dropdown?.parentNode) this.dropdown.parentNode.removeChild(this.dropdown)
    if (this.textarea) {
      // A leftover mentionOpen would make the inline Enter guard swallow
      // submits after a reconnect remounts the hook on the same element.
      delete this.textarea.dataset.mentionOpen
      this.textarea.style.color = ""
      this.textarea.removeEventListener("input", this._inputHandler)
      this.textarea.removeEventListener("keydown", this._keydownHandler)
      this.textarea.removeEventListener("blur", this._blurHandler)
      this.textarea.removeEventListener("scroll", this._scrollHandler)
    }
    if (this.form) this.form.removeEventListener("submit", this._submitHandler, true)
    if (this.form) this.form.removeEventListener("pointerdown", this._submitButtonHandler, true)
    if (this.form) this.form.removeEventListener("click", this._submitButtonHandler, true)
    if (this.componentRoot) {
      this.componentRoot.removeEventListener("pointerdown", this._beforeComponentInteraction, true)
      this.componentRoot.removeEventListener("change", this._beforeComponentInteraction, true)
    }
  },

  _rememberDraft() {
    this._forgetSubmittedDraft()
    this._preparedSubmitValue = null
    this._lastValue = this.textarea?.value || ""
    this._clearDraftAfterSubmit = false
  },

  _syncDraftToServer() {
    if (!this.textarea) return

    const currentTarget = liveComponentTarget(this.textarea)
    if (!Number.isInteger(currentTarget)) return

    this.componentTarget = currentTarget
    this.pushEventTo(currentTarget, "sync_input", { message: this.textarea.value || "" })
  },

  _restoreDraftAfterPatch() {
    if (!this.textarea) return

    if (this._shouldKeepDraftCleared()) {
      this._clearSubmittedDraft()
      this._clearDraftAfterSubmit = false
      return
    }

    if (!this.textarea.value && this._lastValue) {
      const nativeSetter = Object.getOwnPropertyDescriptor(
        HTMLTextAreaElement.prototype, "value"
      ).set
      nativeSetter.call(this.textarea, this._lastValue)
    } else {
      this._lastValue = this.textarea.value || ""
    }
  },

  _prepareSubmitButtonClear(event) {
    const button = event.target?.closest?.("button[type='submit'],input[type='submit']")
    if (!button || !this.form?.contains(button) || button.disabled) return
    this._prepareSubmitClear()
  },

  _prepareSubmitClear() {
    if (!this.textarea) return

    if (this._preparedSubmitValue === null) {
      this._preparedSubmitValue = this.textarea.value || ""
      if (this.submittedMessageInput) {
        this.submittedMessageInput.value = this._preparedSubmitValue
      }
      // The hidden field keeps its JSON so LiveView still serializes it — this
      // handler runs BEFORE the form is serialized, exactly like the message
      // copy above. Only the in-memory list is reset here; `updated()` wipes
      // the field once the server patch comes back with an empty composer.
      this._submittedMentionRefs = this.mentionRefs
      this.mentionRefs = []
    }

    this._rememberSubmittedDraft()
    this._clearDraftAfterSubmit = true
    this._lastValue = ""
    this._clearSubmittedDraft()
  },

  _acceptSubmittedClear() {
    if (!this.textarea) return
    this._preparedSubmitValue = null
    this._submittedMentionRefs = this.mentionRefs
    this.mentionRefs = []
    this._writeMentionRefs()
    this._clearDraftAfterSubmit = true
    this._rememberSubmittedDraft()
    this._lastValue = ""
    if (this.submittedMessageInput) this.submittedMessageInput.value = ""
    this._clearSubmittedDraft()
  },

  _submittedDraftKey() {
    return this.textarea?.id || null
  },

  _rememberSubmittedDraft() {
    const key = this._submittedDraftKey()
    if (!key) return
    window.__seivaDrawerSubmittedDrafts = window.__seivaDrawerSubmittedDrafts || {}
    window.__seivaDrawerSubmittedDrafts[key] = true
  },

  _forgetSubmittedDraft() {
    const key = this._submittedDraftKey()
    if (!key || !window.__seivaDrawerSubmittedDrafts) return
    delete window.__seivaDrawerSubmittedDrafts[key]
  },

  _shouldKeepDraftCleared() {
    const key = this._submittedDraftKey()
    return (
      this.textarea.dataset.chatSubmitting === "true" ||
      this._clearDraftAfterSubmit ||
      (key && window.__seivaDrawerSubmittedDrafts?.[key] === true)
    )
  },

  _clearSubmittedDraft() {
    this._setTextareaValue("")
    this._lastValue = ""
    this._resize()
    this._syncOverlay()
    this._close()
  },

  _setTextareaValue(value) {
    const nativeSetter = Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype, "value"
    ).set
    nativeSetter.call(this.textarea, value)
  },

  _resize() {
    const el = this.textarea
    const maxLines = parseInt(el.dataset.maxLines || "3")
    const lh = parseInt(getComputedStyle(el).lineHeight) || 22
    const pt = parseInt(getComputedStyle(el).paddingTop) || 0
    const pb = parseInt(getComputedStyle(el).paddingBottom) || 0
    const bt = parseInt(getComputedStyle(el).borderTopWidth) || 0
    const bb = parseInt(getComputedStyle(el).borderBottomWidth) || 0
    const maxH = maxLines * lh + pt + pb + bt + bb
    el.style.height = "auto"
    const h = Math.min(el.scrollHeight, maxH)
    el.style.height = h + "px"
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden"
    if (this.overlay) {
      this.overlay.style.height = el.offsetHeight + "px"
      this.overlay.style.overflowY = el.style.overflowY
    }
  },

  // Build a combined list of all known @mention names with their colors for
  // overlay highlighting. Longest first to avoid prefix collisions.
  _buildKnownMentions() {
    const mentions = []
    const push = (name, color) => {
      // A null/empty name used to crash sort() inside updated(), which aborted
      // the LiveView DOM patch and made just-sent chat messages invisible until
      // the user refreshed. Filter aggressively here.
      if (typeof name === "string" && name.length > 0) {
        mentions.push({ name, color })
      }
    }
    this.allUsers.forEach(u => push(u.name, u.avatar_color || "var(--s-300)"))
    this.allAgents.forEach(a => push(a.name, "var(--warn)"))
    this.allSkills.forEach(s => push(s.name, "var(--info)"))
    this.allTools.forEach(t => push(t.name, "var(--info)"))
    this.allApps.forEach(a => push(a.name, "var(--s-300)"))
    this.allWorkflows.forEach(w => push(w.name, "var(--badge-workflow)"))
    this.allCollections.forEach(c => push(c.label || c.collection, "var(--badge-collection)"))
    this.allContextBlocks.forEach(b => push(b.name, "var(--badge-context-block)"))
    return mentions.sort((a, b) => b.name.length - a.name.length)
  },

  _syncOverlay() {
    if (!this.overlay) return
    const val = this.textarea.value
    if (!val) {
      this.overlay.innerHTML = ""
      return
    }

    const esc = val.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

    const known = this._buildKnownMentions()
    let highlighted = esc
    if (known.length > 0) {
      const escaped = known.map(m => m.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      const re = new RegExp("@(" + escaped.join("|") + ")(?=\\s|$)", "g")
      highlighted = esc.replace(re, (_m, name) => {
        const m = known.find(k => k.name === name)
        const color = m ? m.color : "var(--s-300)"
        return `<span class="seiva-mention-overlay-token" style="--mention-color:${color}">@${name}</span>`
      })
    }

    this.overlay.innerHTML = highlighted
    this.overlay.scrollTop = this.textarea.scrollTop
  },

  _createDropdown() {
    const dd = document.createElement("div")
    dd.className = "drawer-mention-dropdown"
    Object.assign(dd.style, {
      display: "none", position: "fixed", zIndex: "10001",
      maxHeight: "240px", overflowY: "auto", borderRadius: "8px",
      border: "1px solid var(--border-default)", background: "var(--bg-card)",
      boxShadow: "0 4px 16px rgba(0,0,0,0.12)", padding: "4px 0",
    })

    dd.addEventListener("mousedown", (e) => {
      e.preventDefault()
      const item = e.target.closest(".dm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx >= 0 && idx < this.candidates.length) {
        this._skipNextBlur = true
        this._select(this.candidates[idx])
      }
    })

    dd.addEventListener("mouseover", (e) => {
      const item = e.target.closest(".dm-item")
      if (!item) return
      const idx = parseInt(item.dataset.idx)
      if (idx !== this.selectedIdx) {
        this.selectedIdx = idx
        this._highlightSelected()
      }
    })

    document.body.appendChild(dd)
    this.dropdown = dd
  },

  _positionDropdown() {
    const rect = this.textarea.getBoundingClientRect()
    Object.assign(this.dropdown.style, {
      left: rect.left + "px",
      width: rect.width + "px",
      bottom: (window.innerHeight - rect.top + 4) + "px",
      top: "auto",
    })
  },

  _detectMention() {
    const val = this.textarea.value
    const caret = this.textarea.selectionStart
    let scanPos = caret - 1
    let foundAt = -1

    while (scanPos >= 0) {
      const ch = val[scanPos]
      if (ch === "\n") break

      if (ch === "@") {
        const before = scanPos >= 1 ? val[scanPos - 1] : " "
        if (before === " " || before === "\n" || scanPos === 0) {
          foundAt = scanPos
        }
        break
      }
      scanPos--
    }

    if (foundAt === -1) {
      if (this.isOpen) this._close()
      return
    }

    const query = val.slice(foundAt + 1, caret)

    this.mentionStart = foundAt
    this._filter(query)

    if (this.candidates.length > 0) {
      if (!this.isOpen) this._open()
      else { this._renderItems(); this._positionDropdown() }
    } else {
      if (this.isOpen) this._close()
    }
  },

  _onKeydown(e) {
    if (!this.isOpen || this.candidates.length === 0) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this.selectedIdx = Math.min(this.selectedIdx + 1, this.candidates.length - 1)
        this._highlightSelected()
        break
      case "ArrowUp":
        e.preventDefault()
        this.selectedIdx = Math.max(this.selectedIdx - 1, 0)
        this._highlightSelected()
        break
      case "Enter":
      case "Tab":
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        this._select(this.candidates[this.selectedIdx])
        break
      case "Escape":
        e.preventDefault()
        this._close()
        break
    }
  },

  _filter(query) {
    const q = query.toLowerCase()
    // One candidate with a null/empty name must never crash the filter —
    // same class of defect as the null-name sort crash documented in
    // chat_mention.js `_buildKnownMentions`.
    const named = (list, key) =>
      (list || []).filter(x => x && typeof x[key] === "string" && x[key].length > 0)

    const userResults = named(this.allUsers, "name")
      .filter(u => u.name.toLowerCase().includes(q) ||
                   (u.email || "").toLowerCase().includes(q))
      .map(u => ({
        type: "user",
        label: u.name,
        sublabel: u.email,
        value: u.name,
        id: u.id,
        color: u.avatar_color || "var(--s-300)"
      }))

    const agentResults = named(this.allAgents, "name")
      .filter(a => a.name.toLowerCase().includes(q))
      .map(a => ({ type: "agent", label: a.name, value: a.name, id: a.id }))

    const skillResults = named(this.allSkills, "name")
      .filter(s => s.name.toLowerCase().includes(q) ||
                   (s.description || "").toLowerCase().includes(q))
      .map(s => ({
        type: "skill",
        label: s.name,
        sublabel: s.description,
        value: s.name,
        id: s.id
      }))

    const toolResults = named(this.allTools, "name")
      .filter(t => t.name.toLowerCase().includes(q) ||
                   (t.description || "").toLowerCase().includes(q))
      .map(t => {
        const src = t.source && t.source !== "platform" ? ` [${t.source}]` : ""
        return {
          type: "tool",
          label: t.name,
          sublabel: (t.description || "") + src,
          value: t.name
        }
      })

    const appResults = named(this.allApps, "name")
      .filter(a => a.name.toLowerCase().includes(q))
      .map(a => ({ type: "app", label: a.name, value: a.name, id: a.id }))

    const workflowResults = named(this.allWorkflows, "name")
      .filter(w => w.name.toLowerCase().includes(q))
      .map(w => ({
        type: "workflow",
        label: w.name,
        sublabel: w.kind === "dag" ? "DAG" : "agent-only",
        value: w.name,
        id: w.id,
        kind: w.kind
      }))

    const collectionResults = (this.allCollections || [])
      .filter(c => c && typeof (c.label || c.collection) === "string" && (c.label || c.collection))
      .filter(c => (c.label || c.collection).toLowerCase().includes(q) ||
                   (c.app_name || "").toLowerCase().includes(q))
      .map(c => ({
        type: "collection",
        label: c.label || c.collection,
        sublabel: c.app_name || "collection",
        value: c.label || c.collection,
        id: c.app_id
      }))

    // A block that is still OFF for this chat is offered too — mentioning
    // it switches it on server-side (`enable_mentioned_context_blocks/2`).
    // The sublabel says so, because the row otherwise looks identical.
    const contextBlockResults = named(this.allContextBlocks, "name")
      .filter(b => b.name.toLowerCase().includes(q) ||
                   (b.description || "").toLowerCase().includes(q))
      .map(b => ({
        type: "context_block",
        label: b.name,
        sublabel: b.enabled === false
          ? [b.description, "not in this chat yet"].filter(Boolean).join(" · ")
          : b.description,
        value: b.name,
        id: b.id
      }))

    // Priority: users > agents > skills > tools > apps > workflows >
    // collections > context_blocks — dealt out in rounds, so every kind is
    // visible from "@" alone (see drawer_mention_rank.mjs).
    this.candidates = rankMentionCandidates([
      userResults,
      agentResults,
      skillResults,
      toolResults,
      appResults,
      workflowResults,
      collectionResults,
      contextBlockResults
    ], 12)
    this.selectedIdx = 0
  },

  _open() {
    this.isOpen = true
    this.textarea.dataset.mentionOpen = "true"
    this.dropdown.style.display = "block"
    this._positionDropdown()
    this._renderItems()
  },

  _close() {
    this.isOpen = false
    delete this.textarea.dataset.mentionOpen
    this.dropdown.style.display = "none"
    this.mentionStart = -1
    this.candidates = []
  },

  _renderItems() {
    const ICON_ATTRS = `fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"`
    const SPARKLES_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9L12 3z"/><path d="M19 16v4"/><path d="M21 18h-4"/></svg>`
    const SKILL_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" ${ICON_ATTRS}><circle cx="12" cy="12" r="3"/><path d="M12 2v3"/><path d="M12 19v3"/><path d="M4.9 4.9 7 7"/><path d="m17 17 2.1 2.1"/><path d="M2 12h3"/><path d="M19 12h3"/><path d="M4.9 19.1 7 17"/><path d="m17 7 2.1-2.1"/></svg>`
    const WRENCH_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M14.7 6.3a5 5 0 0 0 5.6 5.6l-7.9 7.9a3 3 0 0 1-4.2-4.2l7.9-7.9z"/><path d="M4 20l5.5-5.5"/></svg>`
    const APP_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="m12 3 8 4.5-8 4.5-8-4.5L12 3z"/><path d="m4 12 8 4.5 8-4.5"/><path d="m4 16.5 8 4.5 8-4.5"/></svg>`
    const BOLT_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M13 2 4 14h7l-1 8 10-13h-7l1-7z"/></svg>`
    const BOOK_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" ${ICON_ATTRS}><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H20v18H7.5A3.5 3.5 0 0 0 4 23V5.5z"/><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2H20"/><path d="M8 6h8"/></svg>`
    const TABLE_SVG = `<svg width="13" height="13" viewBox="0 0 24 24" ${ICON_ATTRS}><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18"/><path d="M9 10v10"/><path d="M15 10v10"/></svg>`

    this.dropdown.innerHTML = this.candidates.map((c, i) => {
      const sel = i === this.selectedIdx ? " dm-sel" : ""
      const sublabel = c.sublabel
        ? `<span style="font-size:10px;color:var(--text-tertiary);margin-left:6px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">— ${this._escape(c.sublabel)}</span>`
        : ""

      const row = (badge, text) =>
        `<div class="dm-item${sel}" data-idx="${i}" style="padding:6px 10px;cursor:pointer;display:flex;align-items:center;gap:8px;border-radius:4px;margin:2px 4px;">
          ${badge}
          <span style="font-size:12px;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;min-width:0">${text}</span>
        </div>`

      if (c.type === "user") {
        const initial = c.label.charAt(0).toUpperCase()
        const color = c.color || "var(--s-300)"
        const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;background:color-mix(in srgb, ${color} 10%, transparent);color:${color};border:1px solid color-mix(in srgb, ${color} 20%, transparent);font-size:10px;font-weight:600;flex-shrink:0">${initial}</span>`
        return row(badge, this._escape(c.label) + sublabel)
      }

      if (c.type === "agent") {
        const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:var(--warn);color:white;flex-shrink:0">
          ${SPARKLES_SVG}
        </span>`
        return row(badge, this._escape(c.label) + sublabel)
      }

      if (c.type === "skill") {
        const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:var(--info);color:white;flex-shrink:0">
          ${SKILL_SVG}
        </span>`
        return row(badge, this._escape(c.label) + sublabel)
      }

      if (c.type === "tool") {
        const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:var(--info);color:white;flex-shrink:0">
          ${WRENCH_SVG}
        </span>`
        return row(badge, this._escape(c.label) + sublabel)
      }

      if (c.type === "app") {
        const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:var(--s-300);color:white;flex-shrink:0">
          ${APP_SVG}
        </span>`
        return row(badge, this._escape(c.label) + sublabel)
      }

      if (c.type === "workflow") {
        const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:var(--badge-workflow);color:white;flex-shrink:0">
          ${BOLT_SVG}
        </span>`
        return row(badge, this._escape(c.label) + sublabel)
      }

      if (c.type === "context_block") {
        // hero-book-open (matches the Knowledge panel icon in the drawer header)
        const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:var(--badge-context-block);color:white;flex-shrink:0">
          ${BOOK_SVG}
        </span>`
        return row(badge, this._escape(c.label) + sublabel)
      }

      // collection (default fallback)
      const badge = `<span style="display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:6px;background:var(--badge-collection);color:white;flex-shrink:0">
        ${TABLE_SVG}
      </span>`
      return row(badge, this._escape(c.label) + sublabel)
    }).join("")
  },

  _escape(s) {
    return String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  },

  _highlightSelected() {
    this.dropdown.querySelectorAll(".dm-item").forEach((el, i) => {
      el.classList.toggle("dm-sel", i === this.selectedIdx)
    })
  },

  _select(candidate) {
    if (!candidate) {
      // A stale/empty selection must not leave the dropdown (and the
      // `mentionOpen` Enter guard) stuck — close so Enter submits again.
      this._close()
      return
    }
    const val = this.textarea.value
    const before = val.slice(0, this.mentionStart)
    const afterCaret = val.slice(this.textarea.selectionStart)
    const insertion = `@${candidate.value} `
    const newVal = before + insertion + afterCaret
    const newCaret = before.length + insertion.length

    this._rememberMentionRef(candidate)

    this._close()

    const nativeSetter = Object.getOwnPropertyDescriptor(
      HTMLTextAreaElement.prototype, "value"
    ).set
    nativeSetter.call(this.textarea, newVal)

    this.textarea.selectionStart = newCaret
    this.textarea.selectionEnd = newCaret
    this.textarea.focus()
    this._resize()

    this.textarea.dispatchEvent(new Event("input", { bubbles: true }))
  },

  // The picker knows the id of everything it offers; keeping it is what makes
  // resolution independent of finding the label again inside the sentence.
  _rememberMentionRef(candidate) {
    this.mentionRefs = rememberMentionRef(this.mentionRefs, candidate)
  },

  // Pruning on every keystroke keeps the hidden field in step with the text
  // without having to watch for edits inside a mention.
  _syncMentionRefs() {
    this.mentionRefs = pruneMentionRefs(this.mentionRefs, this.textarea?.value)
    this._writeMentionRefs()
  },

  _writeMentionRefs() {
    if (!this.mentionRefsInput) return
    this.mentionRefsInput.value = serializeMentionRefs(this.mentionRefs)
  },
}
